import { useEffect } from "react";
import CanonicalTag from '../components/CanonicalTag';
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import CommunityProperty from "../components/CommunityProperty";

const MAIN_TOKENS = [
  { name: "Pre-leased Commercial Hub", yield: "~7%", type: "Commercial", tag: "Earning now", color: "#1B3A2D", light: "#F0F7F3" },
  { name: "Pre-leased Residential", yield: "~5.5%", type: "Residential", tag: "Earning now", color: "#1B3A2D", light: "#F0F7F3" },
  { name: "Under-construction Commercial", yield: "~15%", type: "Appreciation", tag: "Coming soon", color: "#7C3A1A", light: "#FFF4EE" },
  { name: "Under-construction Residential", yield: "~15%", type: "Appreciation", tag: "Coming soon", color: "#7C3A1A", light: "#FFF4EE" },
];

export default function CommunityPage() {
  const { user } = useAuth();

  useEffect(() => {
    document.title = "Studio Apartment Near Mumbai Airport — Fractional Investment | EstateCoin";

    // Meta description
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Invest in a studio apartment near Mumbai CSMT Airport from ₹100. Pool funds with other investors. Earn 6% assured rental income p.a. White Soil Advisors LLP.';

    // OG tags
    const ogTags = {
      'og:title': 'Studio Apartment Near Mumbai Airport — Own a Piece from ₹100',
      'og:description': 'Pool funds with other investors to own a premium studio apartment near Mumbai Airport. 6% assured rental yield. 10-year lease.',
      'og:type': 'website',
      'og:url': 'https://www.estatecoin.in/community',
    };
    Object.entries(ogTags).forEach(([prop, content]) => {
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', prop); document.head.appendChild(tag); }
      tag.content = content;
    });

    // JSON-LD structured data
    const schema = {
      "@context": "https://schema.org",
      "@type": "RealEstateListing",
      "name": "Studio Apartment Near Mumbai CSMT Airport",
      "description": "Premium managed studio apartment in Marol, Andheri East, Mumbai. Fractional ownership from ₹100. 6% assured rental yield p.a. with 15% escalation every 3 years. 10-year lease.",
      "url": "https://www.estatecoin.in/community",
      "image": "https://www.estatecoin.in/assets/properties/trilive_andheri.webp",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Marol, Andheri East",
        "addressLocality": "Mumbai",
        "addressRegion": "Maharashtra",
        "addressCountry": "IN"
      },
      "offers": {
        "@type": "Offer",
        "priceCurrency": "INR",
        "price": "100",
        "description": "Fractional ownership from ₹100. 6% assured rental yield p.a."
      },
      "brand": {
        "@type": "Organization",
        "name": "White Soil Advisors LLP",
        "legalName": "White Soil Advisors LLP",
        "identifier": "AAT-7542"
      }
    };
    let schemaTag = document.getElementById('community-schema');
    if (!schemaTag) { schemaTag = document.createElement('script'); schemaTag.id = 'community-schema'; schemaTag.type = 'application/ld+json'; document.head.appendChild(schemaTag); }
    schemaTag.textContent = JSON.stringify(schema);
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#F8F5F0", fontFamily: "'DM Sans', sans-serif" }}>
      <CanonicalTag/>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');`}</style>

      {/* ── PAGE HERO ── */}
      <div style={{ background: "linear-gradient(135deg, #0D0D0D 0%, #1B3A2D 60%, #0D0D0D 100%)", padding: "64px 24px 48px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "inline-flex", gap: 8, marginBottom: 20 }}>
            <Link to="/properties" style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>Properties</Link>
            <span style={{ color: "rgba(255,255,255,0.2)" }}>›</span>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>Community</span>
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(28px,5vw,52px)", fontWeight: 700, color: "#fff", margin: "0 0 16px", lineHeight: 1.15 }}>
            Two ways to invest.<br />
            <span style={{ color: "#C9A84C" }}>Both earn while you sleep.</span>
          </h1>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.6)", maxWidth: 560, margin: "0 auto", lineHeight: 1.8 }}>
            Buy fractional tokens of existing properties — or pool with other investors to acquire new ones. Either way, your money works from day 1.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px 80px" }}>

        {/* ── COMPARISON STRIP ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, margin: "-24px 0 40px" }}>
          {/* Token investing */}
          <div style={{ background: "#fff", borderRadius: 20, padding: "24px", border: "2px solid #1B3A2D", boxShadow: "0 8px 32px rgba(27,58,45,0.12)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#1B3A2D", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Way 1 — Token Investing</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#111", marginBottom: 8 }}>Buy tokens,<br />earn instantly</div>
            <div style={{ fontSize: 13, color: "#666", lineHeight: 1.7, marginBottom: 16 }}>
              Buy fractional tokens of our 4 existing properties. Rental income starts accruing from day 3. Claim anytime. Exit via marketplace.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {["From ₹100", "Income from day 3", "4 properties live", "Exit anytime"].map(f => (
                <div key={f} style={{ fontSize: 12, color: "#2D5C45", display: "flex", gap: 6 }}>
                  <span style={{ color: "#1B3A2D", fontWeight: 700 }}>✓</span> {f}
                </div>
              ))}
            </div>
            <Link to="/properties" style={{ display: "block", textAlign: "center", padding: "12px", background: "#1B3A2D", color: "#fff", borderRadius: 10, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
              Browse Properties →
            </Link>
          </div>

          {/* Community investing */}
          <div style={{ background: "#fff", borderRadius: 20, padding: "24px", border: "2px solid #C9A84C", boxShadow: "0 8px 32px rgba(201,168,76,0.12)" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#C9A84C", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>Way 2 — Community Pool ✨ New</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: "#111", marginBottom: 8 }}>Pool together,<br />own premium</div>
            <div style={{ fontSize: 13, color: "#666", lineHeight: 1.7, marginBottom: 16 }}>
              We shortlist premium properties you couldn't buy alone. Pool funds with other investors. Money earns during pooling. You get ownership certificate on acquisition.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {["Earn during pooling too", "Premium properties", "Legal acquisition", "Digital certificate"].map(f => (
                <div key={f} style={{ fontSize: 12, color: "#7C5C1A", display: "flex", gap: 6 }}>
                  <span style={{ color: "#C9A84C", fontWeight: 700 }}>✓</span> {f}
                </div>
              ))}
            </div>
            <a href="#community-section" style={{ display: "block", textAlign: "center", padding: "12px", background: "linear-gradient(135deg, #C9A84C, #E8C97A)", color: "#1B3A2D", borderRadius: 10, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>
              See Community Properties ↓
            </a>
          </div>
        </div>

        {/* ── EXISTING 4 TOKENS — QUICK OVERVIEW ── */}
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 700, color: "#111" }}>Our 4 live properties</div>
              <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>Buy tokens directly — no pooling needed</div>
            </div>
            <Link to="/properties" style={{ fontSize: 13, fontWeight: 600, color: "#1B3A2D", textDecoration: "none", border: "1.5px solid #1B3A2D", padding: "8px 16px", borderRadius: 10 }}>
              View all →
            </Link>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
            {MAIN_TOKENS.map(t => (
              <div key={t.name} style={{ background: "#fff", borderRadius: 14, padding: "16px 20px", border: "1px solid #EDE4DC", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: t.light, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                  🏢
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111", marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: "#888" }}>{t.type}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: t.color }}>{t.yield}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: t.tag === "Earning now" ? "#22c55e" : "#f97316", background: t.tag === "Earning now" ? "#F0FDF4" : "#FFF7ED", padding: "2px 8px", borderRadius: 100, marginTop: 2 }}>{t.tag}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── DIVIDER ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 40 }}>
          <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, transparent, #EDE4DC)" }} />
          <div style={{ padding: "8px 20px", background: "#1B3A2D", borderRadius: 100, fontSize: 12, fontWeight: 700, color: "#C9A84C", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
            ✨ COMMUNITY PROPERTIES
          </div>
          <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg, #EDE4DC, transparent)" }} />
        </div>

        {/* ── COMMUNITY PROPERTIES ── */}
        <div id="community-section">
          <CommunityProperty user={user} />
        </div>
      </div>
    </div>
  );
}
